import { useEffect, useState } from 'react';
import {
  BuildingIcon,
  CasinoIcon,
  DefenseIcon,
  HelpIcon,
  HudShell,
  LoginPrompt,
  NavMenu,
  OverviewIcon,
  ResearchIcon,
  ResourceBar,
  RadarIcon,
  SectorMapIcon,
  SettingsIcon,
  ShipyardIcon,
  ShopIcon,
  TradeIcon,
} from '@spacegame/ui';
import { useGame, useGameActions } from './game/GameProvider';
import { GuidedTour, HelpCenterModal, TutorialProvider, WelcomeModal, useTutorial } from './tutorial';
import { resourceBarModel } from './game/selectors';
import { OverviewView } from './views/OverviewView';
import { BuildingsView } from './views/BuildingsView';
import { ResearchView } from './views/ResearchView';
import { HangarView } from './views/HangarView';
import { SectorView } from './views/SectorView';
import { TradeView } from './views/TradeView';
import { CasinoView } from './views/CasinoView';
import { ShopView } from './views/ShopView';
import { SettingsView } from './views/SettingsView';
import { CampsView } from './views/CampsView';
import { MessagesView } from './views/MessagesView';
import { MessageIcon } from './components/MessageIcon';
import {
  ActivityFeedPanel,
  DebrisPanel,
  EventPanel,
  MissionPanel,
  MissionPanel_Onboarding,
  QueuePanel,
  Toasts,
} from './components/SidePanels';
import { ResourceStatsPanel, type StatsResource } from './components/ResourceStatsPanel';
import { dwLogoWordmark } from '@spacegame/assets/brand';

type ViewId =
  | 'overview' | 'buildings' | 'research' | 'shipyard'
  | 'defense' | 'sector' | 'camps' | 'trade' | 'casino' | 'shop' | 'messages' | 'settings';

/**
 * `useTutorial()` below needs `<TutorialProvider>` above it in the tree, so
 * `App` mounts its own provider rather than requiring every call site
 * (`main.tsx`, every test harness) to remember to wrap it — the tutorial is
 * an implementation detail of the app shell, not something callers opt into.
 */
export function App() {
  return (
    <TutorialProvider>
      <AppShell />
    </TutorialProvider>
  );
}

function AppShell() {
  const { game, clock, authStatus, unreadMessages } = useGame();
  const actions = useGameActions();
  const tutorial = useTutorial();
  const [view, setView] = useState<ViewId>('overview');
  const [stats, setStats] = useState<StatsResource | null>(null);
  const [loginPromptDismissed, setLoginPromptDismissed] = useState(false);
  const loginPromptOpen = authStatus?.mode === 'anonymous' && !loginPromptDismissed;
  const model = resourceBarModel(game, clock());
  const [galaxyTarget, setGalaxyTarget] = useState<{ ring: 1 | 2 | 3 | 4 | 5; system: number; position: number } | null>(null);

  useEffect(() => {
    const onNavigate = (event: Event) => {
      const detail = (event as CustomEvent<{ target?: { ring?: number; system?: number; position?: number } }>).detail;
      const target = detail?.target;
      if (!target || ![1, 2, 3, 4, 5].includes(target.ring ?? 0) || !target.system || !target.position) return;
      setGalaxyTarget({ ring: target.ring as 1 | 2 | 3 | 4 | 5, system: target.system, position: target.position });
      setView('sector');
    };
    window.addEventListener('spacegame:navigate-galaxy', onNavigate);
    return () => window.removeEventListener('spacegame:navigate-galaxy', onNavigate);
  }, []);

  // The guided tour drives which view is on screen for its own steps —
  // it can't call `setView` itself since that state lives here.
  useEffect(() => {
    if (tutorial.pendingView) setView(tutorial.pendingView as ViewId);
  }, [tutorial.pendingView]);

  const navItems = [
    { id: 'overview', label: 'Übersicht', icon: <OverviewIcon size={17} /> },
    { id: 'buildings', label: 'Gebäude', icon: <BuildingIcon size={17} />, badge: game.planets[0]!.buildQueue.length || undefined },
    { id: 'research', label: 'Forschung', icon: <ResearchIcon size={17} />, badge: game.researchQueue.length || undefined },
    { id: 'shipyard', label: 'Werft', icon: <ShipyardIcon size={17} />, badge: game.planets[0]!.shipyardQueue.length || undefined },
    { id: 'defense', label: 'Abwehr', icon: <DefenseIcon size={17} />, alert: game.event?.type === 'comet' },
    { id: 'sector', label: 'Sektor-Karte', icon: <SectorMapIcon size={17} /> },
    {
      id: 'camps',
      label: 'Piratenlager',
      icon: <RadarIcon size={17} />,
      badge: game.camps.length || undefined,
      alert: game.fleetMissions.some((mission) => mission.kind === 'campAttack'),
    },
    {
      id: 'trade',
      label: 'Händler',
      icon: <TradeIcon size={17} />,
      badge: game.market.offers.length || undefined,
      disabled: game.planets[0]!.buildings.tradeRelay < 1,
      lockedHint: 'Benötigt eine Händler-Relaisstation (Stufe 1).',
    },
    {
      id: 'casino',
      label: 'Casino',
      icon: <CasinoIcon size={17} />,
      disabled: game.planets[0]!.buildings.casino < 1,
      lockedHint: 'Benötigt ein Casino (Stufe 1, erfordert eine Händler-Relaisstation).',
    },
    { id: 'shop', label: 'Shop', icon: <ShopIcon size={17} /> },
    { id: 'messages', label: 'Nachrichten', icon: <MessageIcon />, badge: unreadMessages || undefined },
    { id: 'help', label: 'Hilfe', icon: <HelpIcon size={17} /> },
    { id: 'settings', label: 'Einstellungen', icon: <SettingsIcon size={17} /> },
  ];

  // "Hilfe" isn't a view — it opens the Help Center modal over whatever is
  // currently on screen rather than switching `view`, so it's intercepted
  // here instead of reaching `setView`.
  const handleNavChange = (id: string) => {
    if (id === 'help') {
      tutorial.openHelp();
      return;
    }
    setView(id as ViewId);
  };

  return (
    <HudShell
      topBar={
        <ResourceBar
          sticky
          entries={model.entries}
          onOpenStats={(resource) => setStats(resource as StatsResource)}
        />
      }
      sidebar={
        <div className="flex flex-col">
          <div className="flex items-center justify-center border-b border-edge-subtle px-4 py-3.5">
            <img src={dwLogoWordmark} alt="Dyson Warfare" className="h-7 w-auto object-contain" />
          </div>
          <NavMenu items={navItems} value={view} onChange={handleNavChange} />
        </div>
      }
      dock={<NavMenu variant="dock" items={navItems} value={view} onChange={handleNavChange} />}
      aside={
        <>
          <MissionPanel_Onboarding />
          {/* Overview shows its own copy above the fold on mobile (see OverviewView.tsx). */}
          <div className={view === 'overview' ? 'hidden lg:block' : undefined}>
            <QueuePanel />
          </div>
          <EventPanel />
          <DebrisPanel />
          <MissionPanel />
          <ActivityFeedPanel />
        </>
      }
    >
      <Toasts />
      <ResourceStatsPanel resource={stats} onClose={() => setStats(null)} />
      <LoginPrompt
        open={loginPromptOpen}
        onLinkGoogle={() => actions.linkGoogleAccount()}
        onDismiss={() => setLoginPromptDismissed(true)}
        logo={<img src={dwLogoWordmark} alt="Dyson Warfare" className="h-10 w-auto object-contain" />}
      />
      {/* The welcome screen offers to start the guided tour — showing it
          while the login prompt is still up (or before we even know whether
          it will show) stacks two competing first-run modals. Wait until the
          login prompt has been resolved one way or the other. */}
      {authStatus && !loginPromptOpen ? <WelcomeModal /> : null}
      <HelpCenterModal />
      <GuidedTour />
      {view === 'overview' ? (
        <OverviewView onOpenTalents={() => setView('research')} onManagePlanet={() => setView('buildings')} />
      ) : null}
      {view === 'buildings' ? <BuildingsView /> : null}
      {view === 'research' ? <ResearchView /> : null}
      {view === 'shipyard' ? <HangarView kind="ship" /> : null}
      {view === 'defense' ? <HangarView kind="defense" /> : null}
      {view === 'sector' ? <SectorView galaxyTarget={galaxyTarget} /> : null}
      {view === 'camps' ? <CampsView /> : null}
      {view === 'trade' ? <TradeView /> : null}
      {view === 'casino' ? <CasinoView /> : null}
      {view === 'shop' ? <ShopView /> : null}
      {view === 'messages' ? <MessagesView /> : null}
      {view === 'settings' ? <SettingsView /> : null}
    </HudShell>
  );
}
